import { CollapsibleSection } from '@sequencehq/core-components'
import { CustomerContactsField } from '../components/CustomerContactsField'
import { CustomerFormFieldControls } from '../types'
import { useSectionExpansion } from '../useSectionExpansion'

export type ContactsSectionProps = {
  contactsField: CustomerFormFieldControls['contacts']
  /**
   * When true, each contact gets a toggle for whether it receives invoice
   * notifications/reminders. The primary contact's toggle is locked on.
   * Defaults to false.
   */
  showInvoiceRecipientToggle?: boolean
  /**
   * When false, the contacts shown are treated as a subset of a larger roster
   * whose primary contact may not be among them: no contact is auto-promoted
   * to PRIMARY and the primary selector only renders when a shown contact
   * already holds the role. Keep in sync with the hook's
   * `sectionValidation.contacts.requirePrimaryContact`. Defaults to true.
   */
  requirePrimaryContact?: boolean
  /**
   * Index of the contact card to ring as active (e.g. matching a focused field
   * in a side-by-side review). When undefined, no card is ringed.
   */
  activeContactIndex?: number
  'data-testid'?: string
}

export const ContactsSection = ({
  contactsField,
  showInvoiceRecipientToggle,
  requirePrimaryContact,
  activeContactIndex,
  'data-testid': testId
}: ContactsSectionProps) => {
  const { isOpen, onToggle } = useSectionExpansion(
    contactsField.validationErrors.length > 0
  )

  return (
    <CollapsibleSection
      title="Contacts"
      isOpen={isOpen}
      onToggle={onToggle}
      data-testid={testId}
    >
      <CustomerContactsField
        data-testid={testId}
        value={contactsField.value}
        onChange={contactsField.onChange}
        validationErrors={contactsField.validationErrors}
        showInvoiceRecipientToggle={showInvoiceRecipientToggle}
        requirePrimaryContact={requirePrimaryContact}
        activeContactIndex={activeContactIndex}
      />
    </CollapsibleSection>
  )
}
